import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { DocumentPreviewModal } from "../../pages/coordinator/QuickStart.jsx";

/**
 * AddMaterialCard — the post-QuickStart entry point for adding source material to
 * an existing client's profile. Presents the two on-ramps as peers (2-b):
 *
 *   • Add a document — upload one or more non-ISP documents, then "Process" runs
 *     incremental extraction (new docs only, matched against the live record).
 *   • Conduct an interview — links to the existing interview flow.
 *
 * Before this, documents could only be added inside QuickStart (unreachable once
 * a client is set up), so interviews were the only post-setup way to add material.
 * This closes that gap and puts documents and interviews on equal footing.
 *
 * Upload contract mirrors QuickStart: POST /documents/additional (FormData: file +
 * document_type) per file, then POST /documents/process-all to trigger extraction.
 * process-all mode-branches server-side: a client with live content extracts
 * INCREMENTALLY and parks proposals on the contributing doc, which surfaces the
 * "✓ Review" link on the hub.
 *
 * Props:
 *   clientId      — client UUID
 *   onProcessed() — optional; called after process-all is triggered so the hub can
 *                   refetch pending-review and reveal the Review link.
 *   documentsOnly — render just the document lane, without the card chrome or the
 *                   interview lane; for a host that already has its own heading and
 *                   an Interviews panel (the editor hub, Oct 14).
 */

const STATUS_WORD = {
  uploaded:   "not yet processed",
  processing: "processing…",
  extracted:  "awaiting review",
  reviewed:   "in the profile",
  superseded: "superseded",
};

const DOC_TYPES = [
  { value: "family_letter",   label: "Family letter" },
  { value: "shift_notes",     label: "Shift notes" },
  { value: "behavioral_plan", label: "Behavioral plan" },
  { value: "health_plan",     label: "Health plan" },
  { value: "parent_interview", label: "Parent interview (doc)" },
  { value: "other",           label: "Other" },
];

// Types a contributor may pick that the coordinator's own picker doesn't offer.
const CONTRIBUTED_TYPES = {
  clinical_assessment: { label: "Clinical assessment" },
};

export default function AddMaterialCard({ clientId, onProcessed, documentsOnly = false }) {
  const [pendingUploads, setPendingUploads] = useState([]); // [{id, filename, document_type}]
  const [docType, setDocType]   = useState(DOC_TYPES[0].value);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError]       = useState(null);
  const [processed, setProcessed] = useState(false);

  // Guards the background poll: if the coordinator navigates away mid-poll, stop
  // scheduling ticks and stop calling setState on an unmounted component.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Documents on file (Oct 16): what the profile was built from, with a view
  // and a delete — reachable without Quick Start or Start fresh. documentsOnly
  // hosts only (the hub); Quick Start has its own list.
  const [docs, setDocs] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const loadDocs = useCallback(async () => {
    if (!documentsOnly) return null;
    try {
      const r = await fetch(`/api/clients/${clientId}/documents`);
      if (!r.ok) return null;
      const list = await r.json();
      if (mountedRef.current) setDocs(list);
      return list;
    } catch { return null; /* list stays as it was */ }
  }, [clientId, documentsOnly]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  async function deleteDoc(d) {
    const live = d.status === "reviewed";
    const msg = live
      ? `Delete "${d.filename}"? What it added to the profile stays; it just loses its source label. This can't be undone.`
      : `Delete "${d.filename}"? This can't be undone.`;
    if (!window.confirm(msg)) return;
    const r = await fetch(`/api/clients/${clientId}/documents/${d.id}`, { method: "DELETE" }).catch(() => null);
    if (!r || !r.ok) { setError("Couldn't delete that document."); return; }
    setDocs(prev => (prev || []).filter(x => x.id !== d.id));
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("document_type", docType);
      const res = await fetch(`/api/clients/${clientId}/documents/additional`, {
        method: "POST", body: form,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      setPendingUploads(prev => [
        ...prev,
        { id: data.id, filename: file.name, document_type: docType },
      ]);
      setProcessed(false); // new material added — needs processing
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function removeUpload(docId) {
    await fetch(`/api/clients/${clientId}/documents/${docId}`, { method: "DELETE" }).catch(() => {});
    setPendingUploads(prev => prev.filter(d => d.id !== docId));
  }

  async function process() {
    setError(null);
    setProcessing(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/documents/process-all`, {
        method: "POST",
      });
      if (!res.ok && res.status !== 202) throw new Error(`Processing failed (${res.status})`);
      setProcessed(true);
      setPendingUploads([]);
      loadDocs();
      // process-all returns 202 immediately — it only SPAWNS the background
      // extraction; proposals aren't ready yet. A single refetch here would race
      // the background job and lose (see it return {pending:false}), so the Review
      // flag wouldn't appear until a manual refresh. Instead, poll pending-review
      // until it reports something (or we hit the cap), refreshing the hub each
      // time so the flag lights up on its own the moment extraction lands.
      pollForPending();
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
    // NOTE: setProcessing(false) is handled by the poll (on success/timeout), not
    // in a finally — we stay in "processing" while the background job runs.
  }

  function pollForPending() {
    let attempts = 0;
    const maxAttempts = 400;      // 400 × 3s = up to 20 minutes — a long document takes several (Oct 24)
    const intervalMs = 3000;

    async function tick() {
      if (!mountedRef.current) return;
      attempts += 1;
      onProcessed?.();            // refresh the hub's pending-review each poll
      let done = false;
      try {
        const r = await fetch(`/api/clients/${clientId}/pending-review`);
        if (r.ok) {
          const data = await r.json();
          if (data && data.pending) done = true;  // extraction produced proposals
        }
        // Refresh the list too, so a document's row moves on from "not yet
        // processed" as it does (Oct 25: the Process button stayed up for a
        // document already processed). A review left over from an earlier
        // document doesn't count as done while any document is still waiting.
        const list = await loadDocs();
        if (Array.isArray(list) && list.some(d => d.document_type !== "isp"
            && (d.status === "uploaded" || d.status === "processing"))) done = false;
        // No heartbeat for a while (Oct 28): the work stopped, e.g. a server
        // restart. Stop waiting and say so; the Process button comes back.
        if (Array.isArray(list) && list.some(d => d.document_type !== "isp"
            && d.status === "processing" && Number.isFinite(d.heartbeat_age_s) && d.heartbeat_age_s > 90)) {
          setProcessing(false);
          setError("Processing stopped before it finished — usually a server restart. Press Process to start it again.");
          return;
        }
      } catch {
        // transient — keep polling
      }
      if (!mountedRef.current) return;
      if (done || attempts >= maxAttempts) {
        setProcessing(false);
        return;
      }
      setTimeout(tick, intervalMs);
    }
    // first check shortly after spawn, then every intervalMs
    setTimeout(tick, 1500);
  }

  const typeLabel = v => (DOC_TYPES.find(t => t.value === v) || CONTRIBUTED_TYPES[v] || {}).label || v;

  // Uploaded but not processed, and not added in this sitting — in practice a
  // contributor's documents (Oct 24), which wait here for the coordinator. The
  // ISP is left out: Quick Start owns it.
  //
  // Offered only once the profile has been seeded (its ISP reviewed): before
  // that, process-all is the FIRST, combined extraction, which resets the ISP's
  // proposals — not something a waiting letter should trigger. Seeding picks
  // these documents up anyway.
  const seeded  = (docs || []).some(d => d.document_type === "isp" && d.status === "reviewed");
  // A document left "processing" by work that stopped (no heartbeat for 90 s)
  // waits again (Oct 28).
  const stuck   = d => d.status === "processing" && (d.heartbeat_age_s == null || d.heartbeat_age_s > 90);
  const waiting = !seeded ? [] : (docs || []).filter(d => (d.status === "uploaded" || stuck(d)) && d.document_type !== "isp"
                                         && !pendingUploads.some(p => p.id === d.id));

  const docList = documentsOnly && Array.isArray(docs) && (
    <div style={s.docList}>
      <p style={s.laneTitle}>Documents on file{docs.length ? ` (${docs.length})` : ""}</p>
      {docs.length === 0 && <p style={s.laneBody}>None yet.</p>}
      {docs.map(d => (
        <div key={d.id} style={s.fileRow}>
          <span style={s.fileName} title={d.filename}>{d.filename}</span>
          <span style={s.fileType}>
            {typeLabel(d.document_type)} · {d.error ? "failed" : (STATUS_WORD[d.status] || d.status)}
            {d.uploaded_by ? ` · added by ${d.uploaded_by}` : ""}
          </span>
          <button type="button" style={s.linkBtn} onClick={() => setPreviewDoc(d)}>View</button>
          <button type="button" style={s.removeBtn} onClick={() => deleteDoc(d)} title="Delete this document">×</button>
        </div>
      ))}
      {waiting.length > 0 && pendingUploads.length === 0 && !processing && (
        <button
          style={{ ...s.processBtn, ...(processing ? s.btnDisabled : {}) }}
          onClick={process}
          disabled={processing}
        >
          {processing
            ? "Processing…"
            : `Process ${waiting.length} waiting document${waiting.length !== 1 ? "s" : ""} →`}
        </button>
      )}
      {previewDoc && (
        <DocumentPreviewModal clientId={clientId} doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </div>
  );

  const documentLane = (
        <div style={s.lane}>
          {docList}
          {documentsOnly ? (
            <p style={s.laneBody}>
              Upload a family letter, shift notes, a plan — anything with something new in it.
              What's extracted is matched against the profile and waits for your review,
              so nothing already there is overwritten or duplicated without approval.
            </p>
          ) : (
            <p style={s.laneTitle}>Add a document</p>
          )}
          <div style={s.uploadRow}>
            <select value={docType} onChange={e => setDocType(e.target.value)} style={s.select}>
              {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <label style={{ ...s.uploadBtn, ...(uploading ? s.btnDisabled : {}) }}>
              {uploading ? "Uploading…" : "Choose file"}
              <input type="file" onChange={handleFile} disabled={uploading}
                style={{ display: "none" }}
                accept=".pdf,.docx,.txt" />
            </label>
          </div>

          {pendingUploads.length > 0 && (
            <div style={s.fileList}>
              {pendingUploads.map(d => (
                <div key={d.id} style={s.fileRow}>
                  <span style={s.fileName}>{d.filename}</span>
                  <span style={s.fileType}>{typeLabel(d.document_type)}</span>
                  <button style={s.removeBtn} onClick={() => removeUpload(d.id)} title="Remove">×</button>
                </div>
              ))}
              <button
                style={{ ...s.processBtn, ...(processing ? s.btnDisabled : {}) }}
                onClick={process}
                disabled={processing}
              >
                {processing
                  ? "Processing…"
                  : `Process ${pendingUploads.length} document${pendingUploads.length !== 1 ? "s" : ""} →`}
              </button>
            </div>
          )}

          {(processing || processed) && (
            <p style={s.processedNote}>
              {processing
                ? "Processing… extracting proposals — this can take a few minutes. The Review link will appear above automatically when it's ready."
                : <>Processing complete. A <strong>Review</strong> link should now appear above to approve the extracted content.</>}
            </p>
          )}
          {error && <p style={s.error}>{error}</p>}
        </div>
  );

  if (documentsOnly) return documentLane;

  return (
    <div style={s.card}>
      <p style={s.title}>➕ Add to this profile</p>
      <p style={s.body}>
        Add new source material any time. New documents and interviews are matched
        against the existing profile — updating what's there rather than duplicating it.
      </p>

      <div style={s.lanes}>
        {documentLane}

        <div style={s.divider} aria-hidden="true" />

        {/* Interview lane */}
        <div style={s.lane}>
          <p style={s.laneTitle}>Conduct an interview</p>
          <p style={s.laneBody}>
            Gather structured insights from caregivers and family in a guided
            conversation.
          </p>
          <Link to={`/coordinator/clients/${clientId}/interviews`} style={s.interviewLink}>
            🎙 Start an interview →
          </Link>
        </div>
      </div>
    </div>
  );
}

const s = {
  card:     { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  title:    { fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  body:     { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, lineHeight: "var(--leading-normal)" },
  lanes:    { display: "flex", gap: "var(--space-5)", flexWrap: "wrap", marginTop: "var(--space-2)" },
  lane:     { flex: "1 1 260px", display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0 },
  laneTitle:{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  laneBody: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, lineHeight: "var(--leading-normal)" },
  divider:  { width: 1, background: "var(--color-border)", alignSelf: "stretch" },
  uploadRow:{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" },
  select:   { fontSize: "var(--text-sm)", padding: "var(--space-2) var(--space-3)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", background: "var(--color-bg-base)", color: "var(--color-text-primary)" },
  uploadBtn:{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  docList:  { display: "flex", flexDirection: "column", gap: "var(--space-1)", paddingBottom: "var(--space-3)", marginBottom: "var(--space-2)", borderBottom: "1px solid var(--color-border)" },
  linkBtn:  { font: "inherit", fontSize: "var(--text-xs)", color: "var(--color-accent)", background: "none", border: "none", cursor: "pointer", padding: "0 var(--space-1)" },
  fileList: { display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" },
  fileRow:  { display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--text-sm)" },
  fileName: { color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 },
  fileType: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", flexShrink: 0 },
  removeBtn:{ background: "none", border: "none", color: "var(--color-text-tertiary)", cursor: "pointer", fontSize: "var(--text-lg)", lineHeight: 1, padding: "0 var(--space-1)" },
  processBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "#fff", background: "var(--color-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer", alignSelf: "flex-start", marginTop: "var(--space-1)" },
  processedNote: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", margin: 0, marginTop: "var(--space-1)", lineHeight: "var(--leading-normal)" },
  interviewLink: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-accent)", textDecoration: "none", alignSelf: "flex-start", marginTop: "var(--space-1)" },
  error:    { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
};
